import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  Home, CalendarDays, BookOpen, Users, User, ClipboardCheck, Star, 
  Megaphone, Settings, FileText, IdCard, Shield, GraduationCap
} from 'lucide-react';
import './styles.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider, useData } from './context/DataContext';
import { ToastProvider } from './context/ToastContext';
import { Login } from './pages/Login';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { ProfessorDashboard } from './pages/ProfessorDashboard';
import { MyEnrollmentPage } from './pages/MyEnrollmentPage';
import { FrequencyPage } from './pages/FrequencyPage';
import { ProfessorAttendancePage } from './pages/ProfessorAttendancePage';
import { GradesPage } from './pages/GradesPage';
import { ProfessorGradesPage } from './pages/ProfessorGradesPage';
import { ProfessorClassesPage } from './pages/ProfessorClassesPage';
import { ProfessorStudentsPage } from './pages/ProfessorStudentsPage';
import { SecretariaDashboard } from './pages/SecretariaDashboard';
import { NoticesPage } from './pages/NoticesPage';
import { SettingsPage } from './pages/SettingsPage';
import { CoursesPage } from './pages/CoursesPage';
import { MyClassesPage } from './pages/MyClassesPage';
import { HistoryPage } from './pages/HistoryPage';
import { ExamsPage } from './pages/ExamsPage';
import { StudentIDPage } from './pages/StudentIDPage';
import { ReportsPage } from './pages/ReportsPage';

const PROFESSOR_MENU = [
  ['Painel', Home, '/'],
  ['Minhas Turmas', BookOpen, '/minhas-turmas'],
  ['Meus Alunos', Users, '/meus-alunos'],
  ['Frequência', ClipboardCheck, '/frequencia'],
  ['Notas', Star, '/notas'],
  ['Comunicados', Megaphone, '/avisos']
];

const SECRETARIA_MENU = [
  ['Painel Geral', Home, '/'],
  ['Auditoria', Shield, '/?tab=audit'],
  ['Configurações', Settings, '/configuracoes']
];

const STUDENT_MENU = [
  ['Painel', Home, '/'],
  ['Minha Matrícula', FileText, '/matricula'],
  ['Minhas Notas', Star, '/notas'],
  ['Cursos / Matérias', BookOpen, '/cursos'],
  ['Carteirinha', IdCard, '/carteirinha'],
  ['Avisos', Megaphone, '/avisos']
];

function PrivateRoute({ children, type }) {
  const { user } = useAuth();
  const { allEnrollments } = useData();
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Se for aluno, verificar se tem matrícula ativa no banco global
  if (user.type === 'student' && user.enrollmentId) {
    const enrollment = (allEnrollments || []).find(e => e.enrollmentId === user.enrollmentId);
    // Se não encontrou a matrícula, não redireciona imediatamente se o usuário acabou de logar 
    // ou se está em processo de matrícula (embora no login ele já devesse ter).
    // Para evitar telas brancas, vamos permitir o acesso se o usuário existir no Auth.
  }

  if (type && user.type !== type) return <Navigate to="/" replace />;
  
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  
  const menu = user?.type === 'professor' ? PROFESSOR_MENU : 
               user?.type === 'secretaria' ? SECRETARIA_MENU : 
               STUDENT_MENU;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      
      <Route element={<PrivateRoute><Layout menu={menu || STUDENT_MENU} /></PrivateRoute>}>
        <Route path="/" element={
          user?.type === 'professor' ? <ProfessorDashboard /> : 
          user?.type === 'secretaria' ? <SecretariaDashboard /> : 
          <Dashboard />
        } />
        <Route path="/minhas-turmas" element={<ProfessorClassesPage />} />
        <Route path="/meus-alunos" element={<ProfessorStudentsPage />} />
        <Route path="/matricula" element={<MyEnrollmentPage />} />
        <Route path="/aulas" element={<MyClassesPage />} />
        <Route path="/cursos" element={<CoursesPage />} />
        <Route path="/frequencia" element={user?.type === 'professor' ? <ProfessorAttendancePage /> : <FrequencyPage />} />
        <Route path="/notas" element={user?.type === 'professor' ? <ProfessorGradesPage /> : <GradesPage />} />
        <Route path="/historico" element={<HistoryPage />} />
        <Route path="/avaliacoes" element={<ExamsPage />} />
        <Route path="/carteirinha" element={<StudentIDPage />} />
        <Route path="/avisos" element={<NoticesPage />} />
        <Route path="/relatorios" element={<ReportsPage />} />
        <Route path="/configuracoes" element={<SettingsPage />} />
        
        {/* Fallback for other menu items not yet implemented as full pages */}
        <Route path="*" element={
          <div className="fullPageWrapper">
            <div className="panel fullPage" style={{ display: 'grid', placeItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div className="loginIcon" style={{ margin: '0 auto 20px' }}><Settings size={40}/></div>
                <h2>Módulo em Desenvolvimento</h2>
                <p>Esta funcionalidade estará disponível na próxima atualização do sistema.</p>
                <Navigate to="/" replace />
              </div>
            </div>
          </div>
        } />
      </Route>
    </Routes>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, background: '#030712', color: 'white', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', padding: 30, borderRadius: 24, maxWidth: 600 }}>
            <h1 style={{ color: '#ef4444', margin: '0 0 10px' }}>Erro de Renderização</h1>
            <p style={{ color: '#9aa6bb', marginBottom: 20 }}>O portal encontrou um problema inesperado ao carregar esta página.</p>
            <pre style={{ background: 'rgba(0,0,0,0.3)', padding: 15, borderRadius: 12, overflow: 'auto', fontSize: 12, textAlign: 'left', color: '#fca5a5' }}>
              {this.state.error?.message}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              style={{ marginTop: 20, padding: '12px 24px', background: '#8b5cf6', border: 'none', borderRadius: 12, color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Recarregar Portal
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

const rootElement = document.getElementById('root');

if (rootElement) {
  try {
    createRoot(rootElement).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Fatal render error:', error);
    rootElement.innerHTML = `
      <div style="padding: 20px; background: #030712; color: white; min-height: 100vh; font-family: sans-serif;">
        <h1 style="color: #ef4444;">Erro Crítico no Portal</h1>
        <p>Ocorreu um erro ao carregar o portal acadêmico. Por favor, tente limpar o cache do seu navegador.</p>
        <pre style="background: rgba(0,0,0,0.3); padding: 15px; border-radius: 10px; overflow: auto;">${error.message}</pre>
      </div>
    `;
  }
}
